<script setup lang="ts">
  import { IconAgencyStructure } from '@gat-ship/icons'
  import type { Agency } from '@gat-ship/types'
  import {
    agencyStructureKey,
    testIds,
    type AddAgencyRequest
  } from '@gat-ship/types'
  import { globalUtils } from '@gat-ship/utils'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupInput,
    InputGroupTextarea,
    isValidPhoneNumber,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    InputGroupPhone,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import {
    useAgencyApiCalls,
    CountryPicker,
    CompanyPicker
  } from '@gat-ship/ref-data'
  import { inject } from 'vue'

  const isOpen = defineModel<boolean>('open', { default: false })
  const agencyStructureProvider = inject(agencyStructureKey)
  const { checkShortNameAvailabilityRequest } = useAgencyApiCalls()

  const phoneValidators = {
    onBlur: ({ value }: { value: string }) =>
      !value || value.length === 0
        ? undefined
        : isValidPhoneNumber(value)
          ? undefined
          : 'Invalid phone number'
  }

  const addAgencySchema = z.object({
    agencyId: z.string().min(1),
    parentGroupId: z.string().min(1),
    name: z.string().min(1, 'Name is required'),
    shortName: z
      .string()
      .min(1, 'Short Name is required')
      .max(20, 'Max 20 characters'),
    linkedCompanyCode: z.string().min(1, 'Company is required'),
    address: z.string().optional(),
    countryCode: z.string().min(1, 'Please select a country'),
    email: z.string().optional(),
    mobilePhoneNumber: z.string().optional(),
    phoneNumber: z.string().optional()
  })
  type AddAgencyFormValues = z.infer<typeof addAgencySchema>

  const form = useForm({
    defaultValues: {
      agencyId: '',
      parentGroupId: '',
      name: '',
      shortName: '',
      linkedCompanyCode: '',
      address: '',
      countryCode: '',
      email: '',
      mobilePhoneNumber: '',
      phoneNumber: ''
    } as AddAgencyFormValues,
    validators: {
      onSubmit: addAgencySchema
    },
    onSubmit: async ({ value }) => {
      const payload: AddAgencyRequest = {
        agencyId: value.agencyId,
        name: value.name,
        shortName: value.shortName,
        parentGroupId: value.parentGroupId,
        linkedCompanyCode: value.linkedCompanyCode,
        address: value.address || undefined,
        countryCode: value.countryCode,
        email: value.email || undefined,
        mobilePhoneNumber: value.mobilePhoneNumber || undefined,
        phoneNumber: value.phoneNumber || undefined
      }

      await agencyStructureProvider?.commands.addAgency(payload)
      isOpen.value = false
    }
  })

  export interface ShowAddAgencyModalParams {
    parentGroupId: string
    agency?: Agency
  }

  const orEmpty = (value: string | null | undefined) => value ?? ''

  function agencySeed(agency: Agency | undefined) {
    return {
      name: orEmpty(agency?.name),
      shortName: orEmpty(agency?.shortName),
      linkedCompanyCode: orEmpty(agency?.linkedCompany?.code)
    }
  }

  function contactInfoSeed(contactInfo: Agency['contactInfo'] | undefined) {
    return {
      address: orEmpty(contactInfo?.address),
      countryCode: orEmpty(contactInfo?.country?.code),
      email: orEmpty(contactInfo?.email),
      mobilePhoneNumber: orEmpty(contactInfo?.mobilePhoneNumber),
      phoneNumber: orEmpty(contactInfo?.phoneNumber)
    }
  }

  function showModal({ agency, parentGroupId }: ShowAddAgencyModalParams) {
    form.reset({
      agencyId: globalUtils.functions.uuid(),
      parentGroupId,
      ...agencySeed(agency),
      ...contactInfoSeed(agency?.contactInfo)
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen" class="min-w-[400px]">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconAgencyStructure class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Add Agency
      </ModalHeaderTitle>
      <ModalHeaderDescription>
        This will create a new agency.
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <div class="flex flex-col gap-5">
        <ModalSection>
          <FieldGroup>
            <FormField :form="form" name="name" label="Name" required>
              <template #default="{ field }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  autofocus
                  :data-testid="testIds.agency.modal.addAgency.nameInput"
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
            <FormField
              :form="form"
              name="shortName"
              label="Short Name"
              required
              :validators="{
                onChangeAsync: async ({ value }) => {
                  if (!value || value.length === 0) return undefined
                  if (value.length > 20) return 'Max 20 characters'
                  try {
                    const available =
                      await checkShortNameAvailabilityRequest(value)
                    return available
                      ? undefined
                      : 'Short name is already in use.'
                  } catch {
                    return 'Something went wrong while checking short name availability.'
                  }
                },
                onChangeAsyncDebounceMs: 400
              }"
            >
              <template #default="{ field }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  :data-testid="testIds.agency.modal.addAgency.shortnameInput"
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>
        </ModalSection>

        <ModalSection>
          <FieldGroup>
            <FormField
              :form="form"
              name="linkedCompanyCode"
              label="Company"
              required
            >
              <template #default="{ field, invalid }">
                <CompanyPicker
                  :id="field.name"
                  :model-value="field.state.value"
                  :aria-invalid="invalid"
                  :data-testid="testIds.common.select.company"
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>
        </ModalSection>

        <ModalSection>
          <FieldGroup>
            <FormField :form="form" name="address" label="Address">
              <template #default="{ field }">
                <InputGroupTextarea
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  :data-testid="
                    testIds.agency.modal.changeContactInfo.addressInput
                  "
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
            <FormField :form="form" name="countryCode" label="Country" required>
              <template #default="{ field, invalid }">
                <CountryPicker
                  :id="field.name"
                  :model-value="field.state.value"
                  :aria-invalid="invalid"
                  :data-testid="testIds.common.select.country"
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>
          <FieldGroup>
            <FormField :form="form" name="email" label="Email">
              <template #default="{ field }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  :data-testid="
                    testIds.agency.modal.changeContactInfo.emailInput
                  "
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
            <FormField
              :form="form"
              name="mobilePhoneNumber"
              label="Mobile"
              :validators="phoneValidators"
            >
              <template #default="{ field }">
                <InputGroupPhone
                  :id="field.name"
                  :model-value="field.state.value ?? undefined"
                  :data-testid="
                    testIds.agency.modal.changeContactInfo.mobileInput
                  "
                  @update:model-value="(v) => field.handleChange(v ?? '')"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
            <FormField
              :form="form"
              name="phoneNumber"
              label="Phone"
              :validators="phoneValidators"
            >
              <template #default="{ field }">
                <InputGroupPhone
                  :id="field.name"
                  :model-value="field.state.value ?? undefined"
                  :data-testid="
                    testIds.agency.modal.changeContactInfo.phoneInput
                  "
                  @update:model-value="(v) => field.handleChange(v ?? '')"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>
        </ModalSection>
      </div>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Add Agency" :require-dirty="false" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
